/* SUBMIT CASH REPORT — the Preparer's screen (2026-10-08; called Cash Report Import until
   2026-10-15, when Max asked for a name that says what is done here: the figures go in AND go to
   the Reviewer. The route stays /cash/import, because every notice mailed so far links to it).

   Max: "tôi cần 1 module cho việc import nhé và module đó sẽ gồm 3 tab tương ứng với 3 template".
   Tuan's workbook keeps the templates (what goes IN) apart from the reports (what the portal makes
   of it), and so does the portal now: the three tabs are Template 1, 2 and 3, each downloaded,
   filled in and imported — never typed on the Portal (Max, 2026-10-15) — with the Data Check, the
   statements and Send. The Weekly Cash Report, the Monthly Cash Plan and the Month-End
   Reconcile are read, reviewed and approved on their own screens.

   The same report row and the same flow either way — this screen is CashReportPanel in its input
   mode, and every rule about who may import (the entity's Preparer, while the report is open or sent
   back) is the server's, so the two screens cannot disagree about whose move it is.

   There is no list of entities to click through: the entity follows the person (see `choices`). */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Compass, Loader2 } from 'lucide-react';
import Header from './Header';
import TabIcon from './TabIcon';
import CashReportPanel from './CashReportPanel';
import { CASH_TYPES } from './cashTypes';
import { API, call, fmtDay, PeriodBar, TestAsBar } from './cashUi';
import { monthLabel } from './44_CashPlan';
import TourGuide from './TourGuide';
import { ManualButton, MANUAL_STEP } from './ModuleHelp';
import './SPHub.css';
import './ProjectApproval.css';
import './CashSetup.css';
import './CashWeekly.css';

/* The three templates — shared with Cash Report Approval (cashTypes.jsx). */
const TABS = CASH_TYPES;

/* What the period bar says, in each report's own words. NO OPEN / CLOSED WORD AFTER IT (Max,
   2026-10-15: "ko cần 2 text đó á … bị thừa"): a closed period already says so in the panel, in a
   sentence that also says what can still be done, and an open one needs no word at all. */
function PeriodLine({ t, tab, data }) {
  const p = data.period;
  if (tab.type === 'monthend') {
    return (
      <>
        {t('cashm.monthLine', { month: p.label, end: fmtDay(p.end) })}
        {data.rule && <div className="cw-sub cw-rule"><b>{t('cashm.ruleHead', { week: data.rule.week, end: fmtDay(data.rule.weekEnd) })}</b> {data.rule.text}</div>}
      </>
    );
  }
  return (
    <>
      {tab.type === 'plan'
        ? t('xCashMp.planLine', { week: p.code, end: fmtDay(p.end), first: monthLabel(data.months?.[0]), last: monthLabel(data.months?.[(data.months?.length || 1) - 1]) })
        : t('cashw.weekLine', { end: fmtDay(p.end), due: fmtDay(p.deadline) })}
    </>
  );
}

/* THE GUIDE — Max, 2026-10-15: "thêm phần guide như trong sample tracker nhé". The same TourGuide:
   every step points at a control that is on this page and SKIPS one that is not, so the steps that
   only a Preparer sees (Import, Send) and the one only a tester sees (Test as) fall away for
   everybody else without anything here having to know why. Ordered the way a Preparer works: which
   template and which period, where the report stands, the file, the check, Send, then what travels
   with it. */
const TOUR_STEPS = [
  { selector: '[data-tour="ci-tabs"]', title: 'tour.ciTabsT', body: 'tour.ciTabsB' },
  { selector: '.cw-weekbar', title: 'tour.ciPeriodT', body: 'tour.ciPeriodB' },
  { selector: '.cw-steps', title: 'tour.ciStepsT', body: 'tour.ciStepsB' },
  { selector: '[data-tour="ci-template"]', title: 'tour.ciTemplateT', body: 'tour.ciTemplateB' },
  { selector: '[data-tour="ci-import"]', title: 'tour.ciImportT', body: 'tour.ciImportB' },
  { selector: '#cf-form', title: 'tour.ciFormT', body: 'tour.ciFormB' },
  { selector: '.cs-checkbar', title: 'tour.ciCheckT', body: 'tour.ciCheckB' },
  { selector: '.cf-send', title: 'tour.ciSendT', body: 'tour.ciSendB' },
  { selector: '[data-tour="ci-files"]', title: 'tour.ciFilesT', body: 'tour.ciFilesB' },
  { selector: '[data-tour="ci-history"]', title: 'tour.ciHistoryT', body: 'tour.ciHistoryB' },
  { selector: '.cw-testas', title: 'tour.ciTestAsT', body: 'tour.ciTestAsB' },
  MANUAL_STEP,
];
const TOUR_SEEN = 'portalCashImportTourSeen';

export default function CashImport() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const tab = TABS.find((x) => x.type === params.get('tab')) || TABS[0];
  const period = params.get('period') || '';
  const entity = params.get('entity') || '';

  /* ONLY THE NEWEST ANSWER LANDS. The weekly view is the slow one (every entity and its week
     before), so a Template 1 reply could arrive after the Template 3 one it was overtaken by and
     leave a week list under the month tab. Each load takes a number and an answer that is not the
     latest is dropped. */
  const seq = useRef(0);
  const load = useCallback(async () => {
    const mine = ++seq.current;
    try {
      const j = await call(`${API}/view/${tab.type}${period ? `?period=${encodeURIComponent(period)}` : ''}`);
      if (mine !== seq.current) return;
      setData(j); setError('');
    } catch (e) { if (mine === seq.current) setError(e.message); }
  }, [tab.type, period]);
  useEffect(() => { setData(null); load(); }, [load]);
  /* The URL moves one render before the effect above clears the old answer, so "loaded" means the
     answer is FOR this tab — otherwise the panel would mount as a month-end on a week's code and ask
     the server for a report that cannot exist. */
  const ready = !!data && data.type === tab.type && (!period || data.period?.code === period);

  /* Once, unasked, the first time the screen has something to point at — the Sample Tracker's rule:
     written when it ENDS, by Done or Skip alike, and a private window still gets the tour. */
  const [tourOpen, setTourOpen] = useState(false);
  useEffect(() => {
    if (!ready) return undefined;
    let seen = true;
    try { seen = !!localStorage.getItem(TOUR_SEEN); } catch { seen = true; }
    if (seen) return undefined;
    const id = window.setTimeout(() => setTourOpen(true), 600);
    return () => window.clearTimeout(id);
  }, [ready]);
  const closeTour = () => {
    setTourOpen(false);
    try { localStorage.setItem(TOUR_SEEN, '1'); } catch { /* nothing to remember it in */ }
  };

  const setQuery = useCallback((next) => {
    setParams((cur) => {
      const p = new URLSearchParams(cur);
      Object.entries(next).forEach(([k, v]) => { if (v) p.set(k, v); else p.delete(k); });
      return p;
    }, { replace: true });
  }, [setParams]);

  const prepares = useMemo(() => (ready ? data.me?.prepares || [] : []), [ready, data]);
  /* THE ENTITY FOLLOWS THE PERSON (Max, 2026-10-08: "không cần phải click chọn entity … trường
     entity ăn theo user … user có nhiều hơn 2 entity thì có thể drop down để chọn"). It is read from
     the Preparer rows of the Cash Report User list, which is also what gives the right to import —
     so the entity offered and the entity the server accepts cannot disagree. One entity: fixed, no
     choice to make. Several: a dropdown of exactly those. Nobody's Preparer (an Admin, a Developer,
     the Group CFO looking): every entity they may see, read-only, so the screen can still be read. */
  const choices = ready ? (prepares.length ? data.entities.filter((e) => prepares.includes(e.entity)) : data.entities) : [];
  const current = choices.find((e) => e.entity === entity) || choices[0] || null;

  /* A tab is another template, so another kind of period (a week, or a month): the period is left
     to that tab's own default, while the entity stays. Pressing the tab already lit changes nothing,
     or it would throw away the week somebody came in on from a mail. */
  const pickTab = (type) => { if (type !== tab.type) setQuery({ tab: type, period: '' }); };

  const entityPick = current && (
    <label className="ci-ent">
      <span>{t('cashi.entity')}</span>
      {choices.length > 1 ? (
        <select value={current.entity} aria-label={t('cashi.entity')} onChange={(e) => setQuery({ entity: e.target.value })}>
          {choices.map((e) => <option key={e.entity} value={e.entity}>{e.entity} · {e.name}</option>)}
        </select>
      ) : <b className="ci-entfixed" title={current.name}>{current.entity} · {current.name}</b>}
    </label>
  );

  return (
    <div className="sp-page">
      <Header />
      <div className="sp-main cs-page cw-page">
        <div className="ci-titlerow">
          <div className="pa-title">
            <h2>{t('cashi.title')}</h2>
            <p>{t('cashi.sub')}</p>
          </div>
          <div className="mh-bar">
            <button type="button" className="pa-btn ci-guide" title={t('st.tourReopen')} onClick={() => setTourOpen(true)}
              disabled={!ready}>
              <Compass size={14} /> {t('st.tourButton')}
            </button>
            <ManualButton manual="cash-entities" />
          </div>
        </div>
        {data && <TestAsBar t={t} me={data.me} />}

        <div className="pa-tabs cw-tpltabs view-tabs" role="tablist" data-tour="ci-tabs">
          {TABS.map((x) => (
            <button key={x.type} type="button" role="tab" aria-selected={x.type === tab.type}
              className={`pa-tab${x.type === tab.type ? ' active' : ''}`} onClick={() => pickTab(x.type)}>
              <TabIcon k={x.type} /><b>{x.tpl}</b> · {t(x.nav)}
            </button>
          ))}
        </div>

        {error && <div className="pa-banner err">{error}</div>}
        {!error && !ready && <div className="pa-empty"><Loader2 size={16} className="pa-spin" /> {t('cash.loading')}</div>}
        {!error && ready && (
          <>
            <PeriodBar t={t} periods={data.periods} period={data.period} onPick={(code) => setQuery({ period: code })} lead={entityPick}>
              <PeriodLine t={t} tab={tab} data={data} />
            </PeriodBar>
            {/* Said only when it changes what the reader can do — a Preparer's own entity needs no
                sentence under it. */}
            {!prepares.length && <p className="cs-hint ci-who">{t('cashi.notPreparer')}</p>}

            {current ? (
              /* The form, once a version has been IMPORTED (Max, 2026-10-15: "bắt buộc phải download template về
                 và import"; 2026-10-19: "user muốn edit trực tiếp thay vì import lại á"): a report comes in as its
                 template, and is corrected here rather than by importing the file again. The panel draws the form
                 only over a version in the flow, and the server refuses /entry without one. */
              <CashReportPanel key={`${tab.type}|${current.entity}|${data.period.code}`} mode="input" t={t} type={tab.type} Form={tab.Form}
                entity={current.entity} period={data.period.code} onChanged={load} fileKinds={tab.fileKinds}
                heading={(d) => tab.heading(t, d)} renderBody={(d, h) => tab.body(t, d, h)} />
            ) : <div className="pa-empty">{t('cashi.noEntity')}</div>}
          </>
        )}
      </div>
      <TourGuide steps={TOUR_STEPS} open={tourOpen} onClose={closeTour} onFinish={closeTour} />
    </div>
  );
}
